import React, { Component, Fragment } from "react";
import Breadcrumb from "../common/breadcrumb";
import KidoLoader from "../common/KidoLoader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RefreshCw, RotateCcw } from "react-feather";
import { getCurrency, updateCurrency } from "../../firebase/firebase.utils";
import "./currency-kido.css";

const FIELDS = [
  {
    key: "taka",
    title: "Yuan rate",
    lead: "1 RMB (Yuan) =",
    unit: "Taka",
    placeholder: "15",
    hint: "How many Bangladeshi Taka equal 1 Chinese Yuan.",
    tone: "mint",
  },
  {
    key: "shippingFee",
    title: "Shipping fee",
    lead: "1 kg from China =",
    unit: "Taka",
    placeholder: "1200",
    hint: "Shipping charge per kg from China to Bangladesh.",
    tone: "sky",
  },
];

const taka = (n) =>
  "৳ " + Math.round(n).toLocaleString("en-IN");

export class Currency extends Component {
  constructor(props) {
    super(props);
    this.state = {
      taka: "",
      shippingFee: "",
      saved: { taka: "", shippingFee: "" },
      saving: false,
      loaded: false,
      sampleYuan: "100",
      sampleKg: "1",
    };
  }

  componentDidMount = async () => {
    const data = await getCurrency();
    const saved = {
      taka: data && data.taka != null ? String(data.taka) : "",
      shippingFee: data && data.shippingFee != null ? String(data.shippingFee) : "",
    };
    // Show the form even when nothing is stored yet.
    this.setState({ ...saved, saved, loaded: true });
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleReset = () => {
    this.setState({ ...this.state.saved });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { taka, shippingFee } = this.state;
    if (!taka || !shippingFee) {
      toast.error("Both fields are required.");
      return;
    }
    this.setState({ saving: true });
    await updateCurrency({ taka: String(taka), shippingFee: String(shippingFee) });
    this.setState({ saving: false, saved: { taka: String(taka), shippingFee: String(shippingFee) } });
    toast.success("Currency settings updated!");
  };

  render() {
    const { saving, loaded, saved, sampleYuan, sampleKg } = this.state;
    const values = { taka: this.state.taka, shippingFee: this.state.shippingFee };
    const dirty = FIELDS.some((f) => values[f.key] !== saved[f.key]);

    const rate = parseFloat(values.taka) || 0;
    const fee = parseFloat(values.shippingFee) || 0;
    const yuan = parseFloat(sampleYuan) || 0;
    const kg = parseFloat(sampleKg) || 0;
    const productCost = yuan * rate;
    const shipCost = kg * fee;

    return (
      <Fragment>
        <Breadcrumb title="Currency Converter" parent="Localization" />
        <div className="container-fluid">
          <section className="card kd-orders-card kd-fx">
            <div className="kd-ord-top">
              <div className="kd-ord-title">
                <span className="kd-ord-title-tile kd-st--mint" aria-hidden="true">
                  <RefreshCw size={20} />
                </span>
                <div>
                  <h2>Currency converter</h2>
                  <svg className="kd-squiggle" viewBox="0 0 120 10" aria-hidden="true" focusable="false">
                    <path d="M2 6 Q12 1 22 6 T42 6 T62 6 T82 6 T102 6 T118 5" />
                  </svg>
                </div>
              </div>
            </div>
            <p className="kd-fx-sub">
              These two numbers price every China order: the item cost in Taka and its shipping.
            </p>

            {!loaded ? (
              <div className="kd-fx-loading">
                <KidoLoader inline label="Loading rates" />
              </div>
            ) : (
              <div className="kd-fx-body">
                <form className="kd-fx-form" onSubmit={this.handleSubmit}>
                  <div className="kd-fx-tags">
                    {FIELDS.map((field, i) => {
                      const changed = values[field.key] !== saved[field.key];
                      return (
                        <div
                          key={field.key}
                          className={`kd-fx-tag kd-st--${field.tone}` + (changed ? " is-changed" : "")}
                          style={{ "--i": i }}
                        >
                          <span className="kd-fx-tag-hole" aria-hidden="true" />
                          <label className="kd-fx-tag-title" htmlFor={`kd-fx-${field.key}`}>
                            {field.title}
                          </label>
                          <span className="kd-fx-tag-lead">{field.lead}</span>
                          <div className="kd-fx-input">
                            <input
                              id={`kd-fx-${field.key}`}
                              type="number"
                              name={field.key}
                              value={values[field.key]}
                              onChange={this.handleChange}
                              placeholder={field.placeholder}
                              required
                              min="0"
                              step="any"
                              inputMode="decimal"
                            />
                            <span className="kd-fx-unit">{field.unit}</span>
                          </div>
                          <span className="kd-fx-hint">
                            {changed && saved[field.key] !== ""
                              ? `Saved value: ${saved[field.key]} ${field.unit}`
                              : field.hint}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="kd-fx-actions">
                    <p role="status">
                      {dirty ? "You have unsaved changes." : "These are the rates customers see now."}
                    </p>
                    {dirty && (
                      <button type="button" className="btn btn-light" onClick={this.handleReset}>
                        <RotateCcw size={14} aria-hidden="true" />
                        Undo changes
                      </button>
                    )}
                    <button type="submit" className="btn btn-primary" disabled={saving || !dirty}>
                      {saving ? "Saving…" : "Save rates"}
                    </button>
                  </div>
                </form>

                {/* Try the rates on a sample order before saving (nothing is stored). */}
                <aside className="kd-fx-check" aria-label="Quick check">
                  <h3>Quick check</h3>
                  <p className="kd-fx-check-note">Try the rates above on a sample order.</p>

                  <div className="kd-fx-calc">
                    <label htmlFor="kd-fx-sample-yuan">An item costing</label>
                    <div className="kd-fx-mini">
                      <span aria-hidden="true">¥</span>
                      <input
                        id="kd-fx-sample-yuan"
                        type="number"
                        min="0"
                        step="any"
                        value={sampleYuan}
                        onChange={(e) => this.setState({ sampleYuan: e.target.value })}
                      />
                    </div>
                    <span className="kd-fx-eq">is</span>
                    <span className="kd-price-sticker">{taka(productCost)}</span>
                  </div>

                  <div className="kd-fx-calc">
                    <label htmlFor="kd-fx-sample-kg">A parcel weighing</label>
                    <div className="kd-fx-mini">
                      <input
                        id="kd-fx-sample-kg"
                        type="number"
                        min="0"
                        step="any"
                        value={sampleKg}
                        onChange={(e) => this.setState({ sampleKg: e.target.value })}
                      />
                      <span aria-hidden="true">kg</span>
                    </div>
                    <span className="kd-fx-eq">ships for</span>
                    <span className="kd-price-sticker">{taka(shipCost)}</span>
                  </div>

                  <div className="kd-fx-total">
                    <span>Together</span>
                    <strong>{taka(productCost + shipCost)}</strong>
                  </div>
                </aside>
              </div>
            )}
          </section>
        </div>
        <ToastContainer />
      </Fragment>
    );
  }
}

export default Currency;
